import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import Multilingual from '~~utils/Multilingual';
import Button from '~~elements/Button';
import Input from '~~elements/Input';
import UploadField from '~~elements/UploadField';
import { GameSectionDiv, StyledForm } from '../styled';

const StyledDiv = styled.div`
  width: 100%;
  .news {
    &--cover {
      position: relative;
      width: 75%;

      &:after {
        content: '';
        display: block;
        padding-bottom: 75%;
      }
      .close {
        position: absolute;
        font-size: 1.5rem;
        text-shadow: 1px 1px 1px white;
        top: 0;
        right: 1rem;
        cursor: pointer;
      }
      > img {
        position: absolute;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  .upload-area {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #eee;
  }

  .box {
    .input-field {
      width: 100%;
    }
  }

  textarea {
    width: 100%;
    min-height: 200px;
  }
`;

const getNewsData = (news) => {
  const initState = {
    title: '',
    image_path: '',
    image_url: '',
    preview_image_path: '',
    preview_image_url: '',
    content: '',
    source: {
      author: '',
      interviewer: '',
      city: '',
      photographer: '',
    },
  };

  if (!news) {
    return initState;
  }

  return {
    ...initState,
    ...news,
    source: {
      ...initState.source,
      ...news.source,
    },
  };
};

const GameNewsSection = ({ news, onChange, apiInProgress }) => {
  const [currentNews, setCurrentNews] = useState(getNewsData(news));

  useEffect(() => {
    setCurrentNews(getNewsData(news));
  }, [news]);

  useEffect(() => {
    const originNews = getNewsData(news);
    const keyCompare = ['title', 'image_path', 'preview_image_path', 'content'];
    const sourceKeyCompare = ['author', 'interviewer', 'city', 'photographer'];

    let changed = false;
    for (let key of keyCompare) {
      if (currentNews[key] === originNews[key]) {
        continue;
      }

      changed = true;
      break;
    }

    if (!changed) {
      for (let key of sourceKeyCompare) {
        if (currentNews.source[key] === originNews.source[key]) {
          continue;
        }

        changed = true;
        break;
      }
    }

    if (!changed) {
      onChange(null);
      return;
    }

    onChange(currentNews);
  }, [currentNews, news, onChange]);

  const handleCoverRemove = () => {
    setCurrentNews({
      ...currentNews,
      image_path: '',
      image_url: '',
      preview_image_path: '',
      preview_image_url: '',
    });
  };

  const handleCoverUpload = ({ url, path }) => {
    setCurrentNews({
      ...currentNews,
      image_path: path,
      image_url: url,
      preview_image_path: path,
      preview_image_url: url,
    });
  };

  const handleInputOnChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    const indentedKeys = name.split('.');
    const indentedAmount = indentedKeys.length;

    let targetObject = { ...currentNews };
    switch (indentedAmount) {
      case 2:
        targetObject = {
          ...currentNews,
          [indentedKeys[0]]: {
            ...currentNews[indentedKeys[0]],
            [indentedKeys[1]]: value,
          },
        };
        break;
      default:
        targetObject = {
          ...currentNews,
          [indentedKeys[0]]: value,
        };
        break;
    }

    setCurrentNews(targetObject);
  };

  const handleCleanUp = () => {
    setCurrentNews(getNewsData(null));
  };

  return (
    <GameSectionDiv>
      <h3 className="section--title">{Multilingual(`GAME.NEWS.WORDING`)}</h3>
      <div className="section--description">{Multilingual(`TOURNAMENT.NEWS_DESCRIPTION`)}</div>
      <StyledForm onSubmit={(e) => e.preventDefault()}>
        <StyledDiv>
          <div className="box">
            {/* 標題 */}
            <label>{Multilingual(`GAME.NEWS.TITLE`)}</label>
            <div className="input-field">
              <Input width="100%" name="title" value={currentNews.title} onChange={handleInputOnChange} />
            </div>
            {/* 照片 */}
            <div className="news--cover">
              {currentNews.image_url ? (
                <Fragment>
                  <img src={currentNews.image_url} alt="cover" />
                  <i className="close" onClick={handleCoverRemove}>
                    &times;
                  </i>
                </Fragment>
              ) : (
                <label className="upload-area">
                  <UploadField
                    styleType="box"
                    url="/api/media"
                    onChange={handleCoverUpload}
                    customBtn={(onClick) => (
                      <Button nowrap width="unset" color="red" onClick={onClick}>
                        {Multilingual(`TOURNAMENT.MEDIA_UPLOAD`)}
                      </Button>
                    )}
                    maxMB={5}
                    disabled={apiInProgress}
                  />
                </label>
              )}
            </div>
            <div className="input-field --multi-input">
              <div>
                {/* 主編 */}
                <label>{Multilingual(`GAME.NEWS.AUTHOR`)}</label>
                <Input
                  width="100%"
                  name="source.author"
                  value={currentNews.source.author}
                  onChange={handleInputOnChange}
                />
              </div>
              <div>
                {/* 城市 */}
                <label>{Multilingual(`GAME.NEWS.CITY`)}</label>
                <Input width="100%" name="source.city" value={currentNews.source.city} onChange={handleInputOnChange} />
              </div>
              <div>
                {/* 採訪 */}
                <label>{Multilingual(`GAME.NEWS.INTERVIEWER`)}</label>
                <Input
                  width="100%"
                  name="source.interviewer"
                  value={currentNews.source.interviewer}
                  onChange={handleInputOnChange}
                />
              </div>
              <div>
                {/* 攝影 */}
                <label>{Multilingual(`GAME.NEWS.PHOTOGRAPHER`)}</label>
                <Input
                  width="100%"
                  name="source.photographer"
                  value={currentNews.source.photographer}
                  onChange={handleInputOnChange}
                />
              </div>
            </div>
            {/* 內文 */}
            <label>{Multilingual(`GAME.NEWS.CONTENT`)}</label>
            <div className="input-field">
              <textarea name="content" value={currentNews.content} onChange={handleInputOnChange} />
            </div>
            <Button width="unset" color="transparentWithBorder" onClick={handleCleanUp}>
              {Multilingual(`FORM.CLEAN_UP`)}
            </Button>
          </div>
        </StyledDiv>
      </StyledForm>
    </GameSectionDiv>
  );
};

export default GameNewsSection;
